import { StyledAutoComplete, StyledAutocompletePaper } from "@/components/common/AutocompleteCommon";
import UserAvatar from "@/components/lazo-ai-chat-components/Avatar";
import CommonButton, { CommonButtonSecondarySx } from "@/components/utils/CommonButton";
import CommonFormControl from "@/components/utils/CommonFormControl";
import LazoModal from "@/components/utils/LazoModal";
import { CommonStyledSelectMenuProps } from "@/components/utils/SelectStyles";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { LazoPaths } from "@/enums/LazoPaths";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import User from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/User";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { Debouncer } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/Debouncer";
import { useResponsive } from "@/hooks/useResponsive";
import { VirtualDataroom } from "@/models/virtual-data-room/VirtualDataRoom";
import { VirtualDataRoomPermissionType } from "@/models/virtual-data-room/VirtualDataroomPermissionType";
import { DataRoomLazoModalSx } from "@/pages/OneDataRoomPage";
import UserSearchService from "@/services/UserSearchService";
import VirtualDataRoomGuestService, { VirtualDataRoomGuest } from "@/services/VirtualDataRoomGuestService";
import VirtualDataRoomService from "@/services/VirtualDataRoomService";
import { GuestUserDTO } from "@/types/GuestUserDTO";
import { TinyUser } from "@/types/TinyUser";
import { EnumsUtils } from "@/utils/TaskEnumUtils";
import { AddOutlined, Delete as DeleteIcon, ExpandMore } from "@mui/icons-material";
import {
  Box,
  Card,
  FormLabel,
  IconButton,
  ListItem,
  MenuItem,
  SxProps,
  TextField,
  Typography,
  TypographyProps,
  styled,
  typographyClasses,
} from "@mui/material";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import share from "../../../../assets/images/data-room/share.png";
import accesIcon from "../../../../assets/images/data-room/world.png";
import emptyLogo from "../../../../assets/images/no-payments-logo.svg";
import { SimpleButtonContainer } from "../DataRoomAppBar";
import { CommonStyledSelect } from "../DataRoomDirectoryFileTree";
import { GuestPopoverPermission } from "./GuestPopoverPermission";

interface VirtualDataRoomSharingSectionProps {
  virtualDataRoom: VirtualDataroom;
  setVirtualDataRoom: Dispatch<SetStateAction<VirtualDataroom>>;
  open: boolean;
  onClose: (value: boolean) => void;
}

const debouncer = new Debouncer(140);

const columnHelper = createColumnHelper<VirtualDataRoomGuest>();

const EMAIL_REGEX =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

interface MobileGuestCardProps {
  guest: VirtualDataRoomGuest;
  onPermissionChange: (permission: VirtualDataRoomPermissionType) => void;
  onDelete: () => void;
}

function MobileGuestCard({ guest, onPermissionChange, onDelete }: MobileGuestCardProps) {
  const { t } = useTranslation("virtualDataRooms");
  const { isMobile } = useResponsive();
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  return (
    <Card sx={{ p: 2, mb: 2, borderRadius: "0.5rem", border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}` }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
        <InfoContainer
          // @ts-ignore - Some fields are missing from guest but they're not used in this component
          user={guest.guest as User}
          avatarSx={{
            width: "48px",
            height: "48px",
            fontSize: "1rem",
            borderRadius: "0.5rem",
          }}
          nameTypographyProps={{ fontSize: "1rem", fontWeight: 600 }}
          emailTypographyProps={{ fontSize: "0.875rem" }}
        />
        <IconButton
          onClick={() => setOpenDeleteModal(true)}
          sx={{
            color: Colors.HEART_RED,
            padding: { xs: "0.75rem", sm: "0.5rem" },
            minWidth: { xs: "44px", sm: "auto" },
            minHeight: { xs: "44px", sm: "auto" },
            borderRadius: "0.5rem",
            "&:hover": {
              backgroundColor: "rgba(229, 115, 115, 0.08)",
            },
            "&:active": {
              backgroundColor: "rgba(229, 115, 115, 0.16)",
            },
          }}
          size={isMobile ? "medium" : "small"}
        >
          <DeleteIcon fontSize={isMobile ? "medium" : "small"} />
        </IconButton>
      </Box>

      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            mb: 1,
            display: "block",
            fontSize: "0.75rem",
            fontWeight: 500,
            color: Colors.LIGHT_GRAY_8A8E93,
          }}
        >
          {t("sharing.permissions.label") || "Permissions"}
        </Typography>
        <CommonStyledSelect
          value={guest.permissionType}
          onChange={(e) => onPermissionChange(e.target.value as VirtualDataRoomPermissionType)}
          fullWidth
          size={isMobile ? "medium" : "small"}
          MenuProps={{
            ...CommonStyledSelectMenuProps,
            sx: {
              "& .MuiMenuItem-root": {
                minHeight: { xs: "48px", sm: "36px" },
                padding: { xs: "0.75rem 1rem", sm: "0.5rem 1rem" },
              },
            },
          }}
          sx={{
            minHeight: { xs: "44px", sm: "36px" },
            "& .MuiSelect-select": {
              padding: { xs: "0.75rem 1rem", sm: "0.5rem 1rem" },
              minHeight: "auto",
              display: "flex",
              alignItems: "center",
            },
          }}
          renderValue={(v: VirtualDataRoomPermissionType) => (
            <Typography fontSize={{ xs: "0.875rem", sm: "0.75rem" }} fontWeight="400">
              {EnumsUtils.parseEnumToRender(v)}
            </Typography>
          )}
        >
          <MenuItem value={VirtualDataRoomPermissionType.NO_ACCESS}>
            <Box display="flex" alignItems="flex-start" flexDirection="column">
              <Typography fontSize="0.875rem" fontWeight="400">
                {t("sharing.permissions.noAccess.title")}
              </Typography>
              <Typography fontSize="0.75rem" fontWeight="400" color={Colors.LIGHT_GRAY_7A7E82}>
                {t("sharing.permissions.noAccess.description")}
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem value={VirtualDataRoomPermissionType.CAN_VIEW}>
            <Box display="flex" alignItems="flex-start" flexDirection="column">
              <Typography fontSize="0.875rem" fontWeight="400">
                {t("sharing.permissions.canView.title")}
              </Typography>
              <Typography fontSize="0.75rem" fontWeight="400" color={Colors.LIGHT_GRAY_7A7E82}>
                {t("sharing.permissions.canView.description")}
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem value={VirtualDataRoomPermissionType.CAN_DOWNLOAD}>
            <Box display="flex" alignItems="flex-start" flexDirection="column">
              <Typography fontSize="0.875rem" fontWeight="400">
                {t("sharing.permissions.canDownload.title")}
              </Typography>
              <Typography fontSize="0.75rem" fontWeight="400" color={Colors.LIGHT_GRAY_7A7E82}>
                {t("sharing.permissions.canDownload.description")}
              </Typography>
            </Box>
          </MenuItem>
        </CommonStyledSelect>
      </Box>

      <LazoModal
        sx={DataRoomLazoModalSx}
        open={openDeleteModal}
        title={t("sharing.deleteModal.title")}
        handleClose={() => setOpenDeleteModal(false)}
        buttons={[
          <CommonButton
            sx={{
              borderRadius: "0.5rem",
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: "87px" },
              height: "44px",
              minHeight: "44px",
              padding: { xs: "0.75rem 1rem", sm: "0.5rem 1rem" },
            }}
            purpleBorderVariant
            onClick={() => setOpenDeleteModal(false)}
            key="delete-cancel-button"
          >
            {t("sharing.modal.buttons.cancel")}
          </CommonButton>,
          <CommonButton
            sx={{
              borderRadius: "0.5rem",
              backgroundColor: Colors.HEART_RED,
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: "87px" },
              height: "44px",
              minHeight: "44px",
              padding: { xs: "0.75rem 1rem", sm: "0.5rem 1rem" },
              "&:hover": {
                backgroundColor: Colors.HEART_RED,
                opacity: 0.9,
              },
            }}
            onClick={() => {
              onDelete();
              setOpenDeleteModal(false);
            }}
            key="delete-confirm-button"
          >
            {t("common:delete")}
          </CommonButton>,
        ]}
      >
        <Typography fontSize="0.75rem" fontWeight="400" lineHeight="18px" color={Colors.LIGHT_GRAY_8A8E93}>
          {t("sharing.deleteModal.description")}
        </Typography>
      </LazoModal>
    </Card>
  );
}

interface MobileGuestListProps {
  guests: VirtualDataRoomGuest[];
  onPermissionChange: (guest: VirtualDataRoomGuest, permission: VirtualDataRoomPermissionType) => void;
  onDelete: (guest: VirtualDataRoomGuest) => void;
}

function MobileGuestList({ guests, onPermissionChange, onDelete }: MobileGuestListProps) {
  return (
    <Box sx={{ mt: 2 }}>
      {guests.map((guest, index) => (
        <MobileGuestCard
          key={guest.guest.email || index}
          guest={guest}
          onPermissionChange={(permission) => onPermissionChange(guest, permission)}
          onDelete={() => onDelete(guest)}
        />
      ))}
    </Box>
  );
}

export function InfoContainer({
  onClick,
  user,
  avatarSx,
  nameTypographyProps,
  emailTypographyProps,
}: {
  onClick?: () => void;
  user: User;
  avatarSx?: SxProps;
  nameTypographyProps?: TypographyProps;
  emailTypographyProps?: TypographyProps;
}) {
  return (
    <SuggestionInfoContainer onClick={onClick}>
      <UserAvatar sx={{ ...AvatarSx, ...avatarSx }} user={{ firstName: user?.firstName, lastName: user?.lastName } as User} />
      <Box>
        <Typography sx={{ fontWeight: "400", fontSize: "0.75rem" }} {...nameTypographyProps}>
          {user?.firstName + " " + user?.lastName}
        </Typography>
        <Typography color={Colors.LIGHT_GRAY_8A8E93} sx={{ fontWeight: "400", fontSize: "0.625rem" }} {...emailTypographyProps}>
          {user?.email}
        </Typography>
      </Box>
    </SuggestionInfoContainer>
  );
}

function VirtualDataRoomSharingSection({ virtualDataRoom, open, onClose, setVirtualDataRoom }: VirtualDataRoomSharingSectionProps) {
  // Hooks
  const { t } = useTranslation("virtualDataRooms");
  const { isMobile } = useResponsive();

  // Context
  const { currentTenantMembership } = useTenantMemberships();

  // State
  const [suggestedUsers, setSuggestedUsers] = useState<TinyUser[] | null>(null);
  const [value, setValue] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [isEmptyUsers, setIsEmptyUsers] = useState<boolean>(false);
  const [openGuestModalPermission, setOpenGuestModalPermission] = useState<boolean>(false);
  const [selectedPermission, setSelectedPermission] = useState<VirtualDataRoomPermissionType>(virtualDataRoom.defaultPermission);
  const [adding, setAdding] = useState<boolean>(false);
  const [guests, setGuests] = useState<VirtualDataRoomGuest[]>([]);
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  const virtualDataRoomAddress = useMemo(
    () => location.protocol + "//" + location.host + LazoPaths.SHARE_BY_LINK + "?vr=" + virtualDataRoom.externalId,
    [virtualDataRoom.externalId]
  );

  // Lifecycle
  useEffect(() => {
    debouncer.executeLastCall(() => fetchSuggestedUsers(value));
  }, [value]);

  useEffect(() => {
    VirtualDataRoomGuestService.getAll(virtualDataRoom.externalId).then((r) => {
      r.onSuccess((c) => {
        c.length != 0 ? setGuests(c) : setIsEmptyUsers(true);
      });
      r.onError((m) => {
        NotificationService.error(t("sharing.messages.errorFetchingGuests"));
        console.log("Error " + m);
      });
    });
  }, [virtualDataRoom.externalId, t]);

  // Handlers
  function copyToClipboard(address: string) {
    navigator.clipboard
      .writeText(address)
      .then(() => NotificationService.info(t("sharing.messages.linkCopied")))
      .catch(() => {});
  }

  function fetchSuggestedUsers(term: string) {
    if (loading) return;
    setLoading(true);
    UserSearchService.searchShareSuggestions(term, currentTenantMembership.tenant.externalId)
      .then((r) => {
        r.onSuccess((content) => {
          guests.forEach((au) => {
            if (content.findIndex((u) => u.externalId === au.guest.externalId) === -1) {
              content.push(au.guest as unknown as TinyUser);
            }
          });
          setSuggestedUsers(content);
        });
        r.onError(() => {});
      })
      .finally(() => setLoading(false));
  }

  function handleAddSuggestion(u: TinyUser) {
    setGuests((prev) => {
      if (!prev.find((user) => user.guest.externalId === u.externalId)) {
        return prev.concat({ guest: u as unknown as GuestUserDTO } as VirtualDataRoomGuest);
      }
      return prev;
    });
  }

  function renderSuggestion(props: React.HTMLAttributes<HTMLLIElement>, u: TinyUser) {
    return (
      <ListItem {...props} style={{ padding: "1.25rem 0.75rem" }}>
        <InfoContainer user={u as User} onClick={() => handleAddSuggestion(u)} />
      </ListItem>
    );
  }

  function handlePermissionChange(vdrg: VirtualDataRoomGuest, permission: VirtualDataRoomPermissionType) {
    const updated: VirtualDataRoomGuest = {
      ...vdrg,
      permissionType: permission,
    };
    setGuests((prev) =>
      prev.map((g) => {
        if (g.guest.externalId === vdrg.guest.externalId) {
          return { ...g, permissionType: permission };
        }
        return g;
      })
    );
    VirtualDataRoomGuestService.updatePermissions(virtualDataRoom.externalId, updated).then(() => {});
  }

  function handleDeleteClick() {
    setOpenConfirmDeleteModal(true);
  }

  function handleDelete(vdrg: VirtualDataRoomGuest) {
    if (!vdrg.guest.externalId) {
      setGuests((prev) => prev.filter((g) => g.guest.email !== vdrg.guest.email));
      setOpenConfirmDeleteModal(false);
      return;
    }

    if (deleting) {
      return;
    }
    setDeleting(true);
    VirtualDataRoomGuestService.delete(virtualDataRoom.externalId, vdrg)
      .then((r) => {
        r.onSuccess(() => {
          setGuests((prev) => prev.filter((g) => g.guest.externalId !== vdrg.guest.externalId));
          setOpenConfirmDeleteModal(false);
        });
        r.onError((m) => {
          NotificationService.error(t("sharing.messages.errorDeleting"));
          console.log("Error: " + m);
        });
      })
      .finally(() => setDeleting(false));
  }

  function handleAddClick() {
    if (adding) return;
    handleAddedUsers(value);
  }

  function handleConfirmAdding() {
    setAdding(true);
    VirtualDataRoomGuestService.createWithPermission(virtualDataRoom.externalId, guests)
      .then((r) => {
        r.onSuccess((g) => {
          setGuests((prev) =>
            prev.map((orig) => {
              const f = g.find((vdrg) => vdrg.guest.email === orig.guest.email);
              if (f) {
                return f;
              }
              return orig;
            })
          );
        });
        r.onError((m) => {
          NotificationService.error(t("sharing.messages.errorCreating"));
          console.log("Error: " + m);
        });
      })
      .finally(() => {
        setAdding(false);
        onClose(false);
      });
  }

  function handleAddedUsers(v: string | TinyUser) {
    let processed: TinyUser;
    const opt = v;
    if (typeof opt === "string") {
      if (EMAIL_REGEX.test(opt)) {
        processed = {
          email: opt,
          firstName: opt,
          lastName: "",
          externalId: "",
          phone: "",
          avatarUrl: "",
        };
      } else {
        NotificationService.warn(t("sharing.messages.invalidEmail"));
        return;
      }
    } else {
      processed = opt;
    }

    if (!processed) {
      return;
    }

    let list: VirtualDataRoomGuest[] = [...guests];
    const vdrg: VirtualDataRoomGuest = {
      guest: {
        email: processed.email,
        firstName: processed.firstName,
        lastName: processed.lastName,
      } as GuestUserDTO,
      permissionType: selectedPermission,
    };
    if (!list.find((cur) => cur.guest.email === processed.email)) {
      list.push(vdrg);
    }

    setGuests(list);
    setValue("");
  }

  const onGlobalWhoCanAccessChange = (p: VirtualDataRoomPermissionType) => {
    if (virtualDataRoom.defaultPermission === p) return;

    VirtualDataRoomService.updateSettings({
      ...virtualDataRoom,
      defaultPermission: p,
    }).then((r) => {
      r.onSuccess((lc) => setVirtualDataRoom(lc));
      r.onError(() => {});
    });
  };

  // Hooks with dependencies on handlers
  const columns = useMemo(
    () => [
      columnHelper.accessor("guest", {
        id: "info-col",
        header: "",
        cell: (info) => (
          <InfoContainer
            avatarSx={{
              width: "40px",
              height: "40px",
              fontSize: "0.875rem",
              borderRadius: "0.5rem",
            }}
            nameTypographyProps={{ fontSize: "0.875rem" }}
            emailTypographyProps={{ fontSize: "0.75rem" }}
            user={info.getValue() as unknown as User}
          />
        ),
        meta: {
          width: "60%",
        },
      }),
      columnHelper.accessor("permissionType", {
        id: "permission-col",
        header: "",
        cell: (info) => (
          <CommonStyledSelect
            IconComponent={ExpandMore}
            value={info.getValue() || selectedPermission}
            defaultValue={info.getValue() || selectedPermission}
            onChange={(v) => handlePermissionChange(info.row.original, v.target.value as VirtualDataRoomPermissionType)}
            MenuProps={CommonStyledSelectMenuProps}
            renderValue={(v: VirtualDataRoomPermissionType) => (
              <Typography fontSize="0.875rem" fontWeight="400">
                {EnumsUtils.parseEnumToRender(v)}
              </Typography>
            )}
          >
            <MenuItem key={VirtualDataRoomPermissionType.NO_ACCESS} value={VirtualDataRoomPermissionType.NO_ACCESS}>
              <Box display="flex" alignItems="flex-start" flexDirection="column">
                <Typography fontSize="0.875rem" fontWeight="400">
                  {t("sharing.permissions.noAccess.title")}
                </Typography>
                <Typography fontSize="0.75rem" fontWeight="400" color={Colors.LIGHT_GRAY_7A7E82}>
                  {t("sharing.permissions.noAccess.description")}
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem key={VirtualDataRoomPermissionType.CAN_VIEW} value={VirtualDataRoomPermissionType.CAN_VIEW}>
              <Box display="flex" alignItems="flex-start" flexDirection="column">
                <Typography fontSize="0.875rem" fontWeight="400">
                  {t("sharing.permissions.canView.title")}
                </Typography>
                <Typography fontSize="0.75rem" fontWeight="400" color={Colors.LIGHT_GRAY_7A7E82}>
                  {t("sharing.permissions.canView.description")}
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem key={VirtualDataRoomPermissionType.CAN_DOWNLOAD} value={VirtualDataRoomPermissionType.CAN_DOWNLOAD}>
              <Box display="flex" alignItems="flex-start" flexDirection="column">
                <Typography fontSize="0.875rem" fontWeight="400">
                  {t("sharing.permissions.canDownload.title")}
                </Typography>
                <Typography fontSize="0.75rem" fontWeight="400" color={Colors.LIGHT_GRAY_7A7E82}>
                  {t("sharing.permissions.canDownload.description")}
                </Typography>
              </Box>
            </MenuItem>
          </CommonStyledSelect>
        ),
        meta: {
          width: "20%",
        },
      }),
      {
        id: "externalId",
        accessorKey: "externalId",
        header: () => {},
        cell: () => {},
        meta: {
          width: "0",
        },
      },
    ],
    [selectedPermission, t, handlePermissionChange]
  );

  const table = useReactTable({
    data: guests,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <LazoModal
      open={open}
      handleClose={() => onClose(false)}
      title={
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            gap: { xs: "0.5rem", sm: "1rem" },
          }}
        >
          <Text
            sx={{
              fontSize: "14px",
              fontWeight: "600",
              color: Colors.BLACK,
            }}
          >
            {t("sharing.modal.title")}
          </Text>
          <Box
            onClick={() => copyToClipboard(virtualDataRoomAddress)}
            sx={{
              display: "flex",
              gap: ".5rem",
              alignItems: "center",
              cursor: "pointer",
              textDecoration: "none",
              transition: "color 0.3s, transform 0.2s",
              padding: { xs: "0.5rem", sm: "0.25rem" },
              borderRadius: "0.375rem",
              minHeight: { xs: "44px", sm: "auto" },
              justifyContent: { xs: "center", sm: "flex-start" },
              "&:hover": {
                color: Colors.DARK_PURPLE,
                transform: "scale(1.05)",
                backgroundColor: "rgba(95, 39, 205, 0.08)",
              },
              "&:active": {
                backgroundColor: "rgba(95, 39, 205, 0.16)",
                transform: "scale(1.02)",
              },
            }}
          >
            <img src={share.src} />
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "600",
                color: Colors.PURPLE,
              }}
            >
              {t("sharing.modal.copyLink")}
            </Typography>
          </Box>
        </Box>
      }
      buttons={
        <>
          <CommonButton
            sx={{
              ...CommonButtonSecondarySx,
              width: { xs: "100%", sm: "87px" },
              height: "44px",
              borderRadius: "0.5rem",
              minHeight: "44px",
              padding: { xs: "0.75rem 1rem", sm: "0.5rem 1rem" },
            }}
            onClick={() => onClose(false)}
          >
            {t("sharing.modal.buttons.cancel")}
          </CommonButton>
          <CommonButton
            sx={{
              width: { xs: "100%", sm: "87px" },
              height: "44px",
              borderRadius: "0.5rem",
              minHeight: "44px",
              padding: { xs: "0.75rem 1rem", sm: "0.5rem 1rem" },
            }}
            onClick={handleConfirmAdding}
          >
            {t("sharing.modal.buttons.confirm")}
          </CommonButton>
        </>
      }
      sx={{
        width: { xs: "95vw", sm: "90vw", md: "518px" },
        maxWidth: { xs: "none", sm: "500px", md: "37.375rem" },
        height: { xs: "90vh", sm: "auto" },
        maxHeight: { xs: "90vh", sm: "80vh", md: "600px" },
        "& .MuiDialog-paper": {
          margin: { xs: "8px", sm: "32px" },
          padding: { xs: "16px", sm: "24px" },
        },
        "& .modal-footer": {
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: "0.75rem", sm: "1rem" },
          width: "100%",
          marginTop: { xs: "1rem", sm: "1.5rem" },
        },
      }}
    >
      <SharingBody>
        <Divider
          sx={{
            width: "100%",
            marginBottom: "1rem",
          }}
        />

        <SharingRow>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: "1rem", md: "1rem" },
              alignItems: { xs: "stretch", md: "center" },
              width: "100%",
              overflow: "hidden",
            }}
          >
            <CommonFormControl
              sx={{
                flex: "1",
                minWidth: 0, // Allows flex item to shrink below its content size
                width: "100%",
                "&& .MuiOutlinedInput-root": {
                  height: "auto",
                  minHeight: "44px",
                },
                "&& .MuiAutocomplete-root": {
                  width: "100%",
                },
                "&& .MuiTextField-root": {
                  width: "100%",
                },
              }}
            >
              <FormLabel>{t("sharing.modal.shareWithUsers")}</FormLabel>
              <StyledAutoComplete
                limitTags={1}
                options={suggestedUsers || []}
                onKeyDown={(ev) => ev.key === "Enter" && handleAddClick()}
                getOptionLabel={(opt: TinyUser) => opt.email}
                renderOption={(props, option: TinyUser) => renderSuggestion(props, option)}
                slotProps={{ paper: { component: StyledAutocompletePaper } }}
                value={{ email: value } as TinyUser}
                isOptionEqualToValue={(opt: TinyUser, val: TinyUser) => opt.externalId === val.email}
                freeSolo
                onChange={(_, v: string | TinyUser) => handleAddedUsers(v)}
                filterOptions={(x: TinyUser[]) => x.filter((u) => guests.findIndex((au) => au.guest.externalId === u.externalId) === -1)}
                inputValue={value}
                onInputChange={(_, v) => setValue(v)}
                renderInput={(params) => (
                  <TextField {...params} placeholder={value.length === 0 ? t("sharing.placeholders.enterToAdd") : ""} />
                )}
              />
            </CommonFormControl>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                paddingTop: { xs: "0", md: "1rem" },
                gap: { xs: "0.75rem", sm: "1rem" },
              }}
            >
              <CommonStyledSelect
                MenuProps={CommonStyledSelectMenuProps}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: "140px" },
                  height: "44px",
                  "&& .MuiSelect-select": {
                    padding: { xs: "0.75rem 1rem", sm: "0.5rem 1rem" },
                    paddingRight: { xs: "2.5rem", sm: "2rem" },
                    minHeight: "auto",
                    display: "flex",
                    alignItems: "center",
                  },
                  "&& .MuiOutlinedInput-root": {
                    height: "44px",
                  },
                }}
                defaultValue={virtualDataRoom.defaultPermission || VirtualDataRoomPermissionType.CAN_VIEW}
                onChange={(ev) => setSelectedPermission(ev.target.value as VirtualDataRoomPermissionType)}
              >
                {Object.keys(VirtualDataRoomPermissionType).map((k) => {
                  if (k === VirtualDataRoomPermissionType.CAN_EDIT) return;
                  return (
                    <MenuItem key={k} value={VirtualDataRoomPermissionType[k]}>
                      {EnumsUtils.parseEnumToRender(VirtualDataRoomPermissionType[k])}
                    </MenuItem>
                  );
                })}
              </CommonStyledSelect>
              <CommonButton
                disabled={adding}
                sx={{
                  ...CommonButtonSecondarySx,
                  borderRadius: "0.5rem",
                  padding: { xs: "0.75rem 1rem", sm: "0.75rem 1rem" },
                  height: "44px",
                  minHeight: "44px",
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: "100px" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  fontSize: { xs: "0.875rem", sm: "0.75rem" },
                  fontWeight: "500",
                }}
                onClick={handleAddClick}
              >
                {adding ? (
                  <Spinner size={"1.5rem"} />
                ) : (
                  <>
                    <AddOutlined sx={{ fontSize: { xs: "1.25rem", sm: "1rem" } }} />
                    <Typography sx={{ fontSize: "inherit", fontWeight: "inherit" }}>{t("sharing.modal.buttons.add")}</Typography>
                  </>
                )}
              </CommonButton>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: ".5rem",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: ".5rem",
                alignItems: "center",
              }}
            >
              <img src={accesIcon.src} />

              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: Colors.BLACK_27282C,
                }}
              >
                {t("sharing.modal.whoCanAccess")}
              </Typography>
            </Box>
            <GuestPopoverPermission
              open={openGuestModalPermission}
              setOpen={setOpenGuestModalPermission}
              onChange={onGlobalWhoCanAccessChange}
              defaultPermission={virtualDataRoom.defaultPermission}
            />
          </Box>
        </SharingRow>
        {guests.length > 0 ? (
          isMobile ? (
            <MobileGuestList guests={guests} onPermissionChange={handlePermissionChange} onDelete={handleDelete} />
          ) : (
            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "1px",
                  boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.45)",
                  zIndex: 1,
                }}
              />

              <FilesStyledModal>
                <thead>
                  {table.getHeaderGroups().map((group) => (
                    <tr key={group.id}>
                      {group.headers.map((header) => (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          style={{
                            width: header.column.columnDef.meta?.width || "auto",
                            minWidth: header.column.columnDef.meta?.minWidth || "auto",
                          }}
                        >
                          <></>
                        </th>
                      ))}
                      {/* Placeholder */}
                      <th style={{ width: "100%" }}></th>
                    </tr>
                  ))}
                </thead>

                <tbody
                  style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    position: "relative", // Para mantener la posición relativa de las sombras
                  }}
                >
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                      ))}
                      <td>
                        <SimpleButtonContainer
                          onClick={handleDeleteClick}
                          outlineColor={Colors.HEART_RED}
                          sx={{
                            color: Colors.HEART_RED,
                            display: "flex",
                            minHeight: "36px",
                            padding: "0.5rem 0.75rem",
                            borderRadius: "0.375rem",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: "rgba(229, 115, 115, 0.08)",
                            },
                            "&:active": {
                              backgroundColor: "rgba(229, 115, 115, 0.16)",
                            },
                          }}
                        >
                          <Typography sx={{ fontSize: "0.75rem", fontWeight: "500" }}>{t("sharing.modal.buttons.remove")}</Typography>
                        </SimpleButtonContainer>
                        <LazoModal
                          sx={DataRoomLazoModalSx}
                          open={openConfirmDeleteModal}
                          title={t("sharing.deleteModal.title")}
                          handleClose={() => setOpenConfirmDeleteModal(false)}
                          buttons={[
                            <CommonButton
                              sx={{
                                borderRadius: "0.5rem",
                                width: { xs: "100%", sm: "auto" },
                                minWidth: { sm: "87px" },
                                height: "44px",
                                minHeight: "44px",
                                padding: { xs: "0.75rem 1rem", sm: "0.5rem 1rem" },
                              }}
                              purpleBorderVariant
                              onClick={() => setOpenConfirmDeleteModal(false)}
                              key="delete-cancel-button"
                            >
                              {t("sharing.modal.buttons.cancel")}
                            </CommonButton>,
                            <CommonButton
                              sx={{
                                borderRadius: "0.5rem",
                                backgroundColor: Colors.HEART_RED,
                                width: { xs: "100%", sm: "auto" },
                                minWidth: { sm: "87px" },
                                height: "44px",
                                minHeight: "44px",
                                padding: { xs: "0.75rem 1rem", sm: "0.5rem 1rem" },
                                "&:hover": {
                                  backgroundColor: Colors.HEART_RED,
                                  opacity: 0.9,
                                },
                                "&:disabled": {
                                  backgroundColor: Colors.HEART_RED,
                                  opacity: 0.6,
                                },
                              }}
                              onClick={() => handleDelete(row.original)}
                              disabled={deleting}
                              key="delete-confirm-button"
                            >
                              {!deleting ? t("common:delete") : <Spinner size={"1.5rem"} />}
                            </CommonButton>,
                          ]}
                        >
                          <Typography fontSize="0.75rem" fontWeight="400" lineHeight="18px" color={Colors.LIGHT_GRAY_8A8E93}>
                            {t("sharing.deleteModal.description")}
                          </Typography>
                        </LazoModal>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </FilesStyledModal>
            </Box>
          )
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "1rem",
              padding: "1rem",
              height: "310px",
            }}
          >
            <img
              src={emptyLogo.src}
              style={{
                width: "110px",
                height: "110px",
              }}
            />
            <Text
              sx={{
                fontSize: "14px",
                fontWeight: "600",
                color: Colors.BLACK,
                padding: "1rem",
              }}
            >
              {t("sharing.modal.emptyState")}
            </Text>
          </Box>
        )}
      </SharingBody>
    </LazoModal>
  );
}

export default VirtualDataRoomSharingSection;

const SharingBody = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  rowGap: "0.5rem",
  width: "100%",
  minHeight: "400px",
  maxHeight: "600px",
  [theme.breakpoints.down("sm")]: {
    rowGap: "1rem",
    minHeight: "auto",
    maxHeight: "none",
  },
}));

const SharingRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  "& .setting-title": {
    fontWeight: "500",
    fontSize: "14px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
  },
  [theme.breakpoints.down("sm")]: {
    gap: "1rem",
  },
}));

const Divider = styled(Box)(() => ({
  width: "100%",
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
}));

const Text = styled(Typography)(({ theme }) => ({
  "&.menu-items": {
    fontSize: "0.875rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1rem",
    },
  },
  "&.first-title": {
    fontSize: "18px",
    fontWeight: "500",
    [theme.breakpoints.down("sm")]: {
      fontSize: "20px",
    },
  },
  "&.share-link": {
    fontSize: ".875rem",
    fontWeight: "600",
    lineHeight: "1.25rem",
    color: Colors.BLACK_27282C,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1rem",
    },
  },
  "&.share-team": {
    fontSize: ".75rem",
    fontWeight: "500",
    lineHeight: "1.125rem",
    color: Colors.LIGHT_GRAY_8A8E93,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.875rem",
    },
  },
  "&.desc": {
    color: Colors.LIGHT_GRAY_8A8E93,
    fontSize: "14px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
  },
  "&.small-desc": {
    color: Colors.LIGHT_GRAY_8A8E93,
    fontSize: "12px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
    },
  },
  "&.text-14": {
    fontSize: "14px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
  },
  "&.text-12": {
    color: Colors.LIGHT_GRAY_7A7E82,
    fontSize: "12px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
    },
  },
}));

const AvatarSx = {
  borderRadius: "0.25rem",
  backgroundColor: "#fff",
  backgroundImage: "none",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  color: Colors.ELEGANT_BLACK,
  fontWeight: 400,
  width: "24px",
  height: "24px",
  fontSize: "0.75rem",
};

const SuggestionInfoContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  width: "100%",
}));

const FilesStyledModal = styled("table")(() => ({
  borderCollapse: "collapse",
  tableLayout: "fixed",
  width: "100%",
  maxHeight: "300px",
  overflowY: "auto",
  display: "block",
  th: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: Colors.LIGHT_GRAY_8A8E93,
    textAlign: "start",
    paddingBottom: "0.75rem",
    ":first-of-type": {
      paddingLeft: "1rem",
    },
    ":last-of-type": {
      paddingRight: "1rem",
    },
  },
  "tr:not(:first-of-type :last-of-type)": {
    //  borderTop: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
    borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
    ":hover": {
      cursor: "pointer",
      backgroundColor: Colors.LIGHT_GRAY_F4F4F4,
    },
  },
  "tr:first-of-type": {
    borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  },
  td: {
    ":first-of-type": {
      paddingLeft: "1rem",
    },
    ":last-of-type": {
      paddingRight: "1rem",
    },
    maxWidth: "100px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    paddingTop: "0.625rem",
    paddingBottom: "0.625rem",
    [`& .${typographyClasses.root}`]: {
      "&.filename-container": {
        overflowX: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: "0.75rem",
        fontWeight: "400",
        paddingRight: "0.5rem",
        lineHeight: "18px",
      },
      fontSize: "0.75rem",
      fontWeight: "400",
    },
  },
}));
